import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { APP_ACCESS, type AppAccess } from "@krut/shared";
import { api, unwrap } from "../api.ts";
import { useBrand } from "../hooks.ts";
import { Badge, Button, Card, ErrorText, Field, Input, PageHeader, Select, Spinner } from "../components/ui.tsx";

const load = () => unwrap(api.me.apps.$get());
type Mine = Awaited<ReturnType<typeof load>>;
type WebApp = Mine["apps"][number];

const accessLabel = (a: AppAccess, brand: string) =>
  ({
    owner: "Only me",
    group: "My group",
    workspace: `Everyone in ${brand}`,
    google: "Anyone with a Google account",
    link: "Anyone with the share link (no sign-in)",
  })[a];

const PUBLIC: AppAccess[] = ["google", "link"];

function Copy({ text }: { text: string }) {
  const [done, setDone] = useState(false);
  return (
    <Button
      type="button"
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => {
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        });
      }}
    >
      {done ? "Copied" : "Copy"}
    </Button>
  );
}

/** The person's published web apps: links, who may open each, share links and their own domains. */
export function WebAppsPage() {
  const q = useQuery({ queryKey: ["web-apps"], queryFn: load });
  if (q.isPending) return <div className="grid place-items-center py-16"><Spinner /></div>;
  if (q.error) return <ErrorText error={q.error} />;
  const { enabled, allowed, apps } = q.data;
  return (
    <>
      <PageHeader
        title="Web apps"
        description="Small apps your assistant built for you — trackers, forms, dashboards — each at its own link. Visitors sign in first, and a new app opens only for you until you share it. To make one, ask your assistant in chat, for example “build me a page to track job applicants”."
      />
      {!enabled ? (
        <Card className="px-5 py-8 text-center text-sm text-[var(--muted)]">Web apps aren't set up in this workspace.</Card>
      ) : !allowed && !apps.length ? (
        <Card className="px-5 py-8 text-center text-sm text-[var(--muted)]">Publishing web apps is switched off for your group. An administrator decides this.</Card>
      ) : !apps.length ? (
        <Card className="px-5 py-8 text-center text-sm text-[var(--muted)]">No apps yet. Ask your assistant to build one, and it appears here once it's published.</Card>
      ) : (
        <div className="space-y-4">
          {!allowed && <Card className="p-4 text-sm text-amber-800">Publishing web apps is switched off for your group, so these apps are offline.</Card>}
          {apps.map((a) => <AppCard key={a.id} app={a} publicAllowed={q.data.publicAllowed} />)}
        </div>
      )}
    </>
  );
}

function AppCard({ app, publicAllowed }: { app: WebApp; publicAllowed: boolean }) {
  const qc = useQueryClient();
  const brand = useBrand();
  const refresh = (next?: WebApp) => {
    if (next) qc.setQueryData<Mine>(["web-apps"], (d) => d && { ...d, apps: d.apps.map((x) => (x.id === next.id ? next : x)) });
    void qc.invalidateQueries({ queryKey: ["web-apps"] });
  };
  const patch = useMutation({
    mutationFn: (json: { access?: AppAccess; name?: string; title?: string; newShareLink?: boolean }) => unwrap(api.me.apps[":id"].$patch({ param: { id: app.id }, json })),
    onSuccess: (v) => refresh(v as WebApp),
  });
  const remove = useMutation({ mutationFn: () => unwrap(api.me.apps[":id"].$delete({ param: { id: app.id } })), onSuccess: () => refresh() });
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(app.name);
  const [title, setTitle] = useState(app.title);

  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="truncate text-base font-semibold">{app.title}</h2>
          <a href={app.url} target="_blank" rel="noreferrer" className="break-all text-sm text-[var(--color-accent)] hover:underline">
            {app.url}
          </a>
          <div className="mt-0.5 text-xs text-[var(--muted)]">Files in {app.folder}</div>
        </div>
        <div className="flex gap-2">
          <Copy text={app.url} />
          <Button variant="ghost" onClick={() => setEditing(!editing)}>{editing ? "Close" : "Rename"}</Button>
        </div>
      </div>

      {editing && (
        <form
          className="mt-4 grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end"
          onSubmit={(e) => {
            e.preventDefault();
            patch.mutate({ title, name }, { onSuccess: () => setEditing(false) });
          }}
        >
          <Field label="Title"><Input value={title} maxLength={80} required onChange={(e) => setTitle(e.target.value)} /></Field>
          <Field label="Link name" hint="The old link stops working.">
            <Input value={name} maxLength={40} required pattern="[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?" onChange={(e) => setName(e.target.value.toLowerCase())} />
          </Field>
          <Button variant="primary" disabled={patch.isPending}>Save</Button>
        </form>
      )}

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <Field
          label="Who can open it"
          hint={
            app.effectiveAccess !== app.access
              ? "Your group doesn't allow sharing outside the workspace any more, so it's open to the workspace only."
              : PUBLIC.includes(app.access)
                ? "People outside the company can open it."
                : undefined
          }
        >
          <Select value={app.access} disabled={patch.isPending} onChange={(e) => patch.mutate({ access: e.target.value as AppAccess })}>
            {APP_ACCESS.map((a) => (
              <option key={a} value={a} disabled={PUBLIC.includes(a) && !publicAllowed}>
                {accessLabel(a, brand.name)}
                {PUBLIC.includes(a) && !publicAllowed ? " (not allowed for your group)" : ""}
              </option>
            ))}
          </Select>
        </Field>
        {app.shareUrl && (
          <Field label="Share link" hint="Anyone with this link can open the app without signing in. A new link stops the old one.">
            <div className="flex gap-2">
              <Input readOnly value={app.shareUrl} onFocus={(e) => e.target.select()} />
              <Copy text={app.shareUrl} />
              <Button type="button" disabled={patch.isPending} onClick={() => confirm("Make a new share link? The current one stops working, and everyone who came in through it is signed out.") && patch.mutate({ newShareLink: true })}>
                New
              </Button>
            </div>
          </Field>
        )}
      </div>
      <ErrorText error={patch.error} />

      <Domains app={app} onChange={refresh} />

      <div className="mt-5 flex justify-end border-t border-[var(--line)] pt-4">
        <Button variant="danger" disabled={remove.isPending} onClick={() => confirm(`Take ${app.title} offline? Its link stops working; its files stay in ${app.folder}.`) && remove.mutate()}>
          Take offline
        </Button>
      </div>
      <ErrorText error={remove.error} />
    </Card>
  );
}

function Domains({ app, onChange }: { app: WebApp; onChange: (v?: WebApp) => void }) {
  const [domain, setDomain] = useState("");
  const add = useMutation({
    mutationFn: () => unwrap(api.me.apps[":id"].domains.$post({ param: { id: app.id }, json: { domain } })),
    onSuccess: (v) => {
      setDomain("");
      onChange(v as WebApp);
    },
  });
  const check = useMutation({ mutationFn: (d: string) => unwrap(api.me.apps[":id"].domains[":domain"].check.$post({ param: { id: app.id, domain: d } })), onSuccess: (v) => onChange(v as WebApp) });
  const drop = useMutation({ mutationFn: (d: string) => unwrap(api.me.apps[":id"].domains[":domain"].$delete({ param: { id: app.id, domain: d } })), onSuccess: (v) => onChange(v as WebApp) });
  return (
    <div className="mt-5">
      <h3 className="text-sm font-medium">Your own domain</h3>
      <p className="mb-3 text-xs text-[var(--muted)]">Open the app at an address of your company's too, e.g. tools.yourcompany.com. Same sign-in.</p>
      <div className="space-y-3">
        {app.domains.map((d) => (
          <div key={d.domain} className="rounded-lg border border-[var(--line)] p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                {d.status === "active" ? (
                  <a href={`https://${d.domain}`} target="_blank" rel="noreferrer" className="text-sm font-medium text-[var(--color-accent)] hover:underline">{d.domain}</a>
                ) : (
                  <span className="text-sm font-medium">{d.domain}</span>
                )}
                <Badge tone={d.status === "active" ? "good" : d.status === "error" ? "bad" : "neutral"}>{d.status === "active" ? "Live" : d.status === "error" ? "Not working" : "Waiting for DNS"}</Badge>
              </div>
              <div className="flex gap-2">
                {d.status !== "active" && <Button disabled={check.isPending} onClick={() => check.mutate(d.domain)}>Check now</Button>}
                <Button variant="ghost" disabled={drop.isPending} onClick={() => confirm(`Disconnect ${d.domain}?`) && drop.mutate(d.domain)}>Remove</Button>
              </div>
            </div>
            {d.detail && <p className="mt-2 text-xs text-[var(--muted)]">{d.detail}</p>}
            {d.records.length > 0 && (
              <div className="mt-2 overflow-x-auto">
                <p className="mb-1 text-xs text-[var(--muted)]">Add these at your DNS provider (on Cloudflare: DNS only, grey cloud). It goes live by itself within a few minutes.</p>
                <table className="w-full text-left text-xs">
                  <thead className="text-[var(--muted)]"><tr><th className="py-1 pr-3 font-medium">Type</th><th className="py-1 pr-3 font-medium">Name</th><th className="py-1 font-medium">Value</th></tr></thead>
                  <tbody className="font-mono">
                    {d.records.map((r) => (
                      <tr key={r.type + r.name + r.value} className="border-t border-[var(--line)]"><td className="py-1 pr-3">{r.type}</td><td className="break-all py-1 pr-3">{r.name}</td><td className="break-all py-1">{r.value}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate();
          }}
        >
          <Input placeholder="tools.yourcompany.com" value={domain} onChange={(e) => setDomain(e.target.value)} required />
          <Button disabled={add.isPending || !domain.trim()}>Connect</Button>
        </form>
        <ErrorText error={add.error ?? check.error ?? drop.error} />
      </div>
    </div>
  );
}

/** Admin → Workspace → Web apps: every published app, whose it is and who may open it. */
export function AdminWebAppsPage() {
  const qc = useQueryClient();
  const brand = useBrand();
  const q = useQuery({ queryKey: ["admin-web-apps"], queryFn: () => unwrap(api.admin.apps.$get()) });
  const remove = useMutation({
    mutationFn: (id: string) => unwrap(api.admin.apps[":id"].$delete({ param: { id } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin-web-apps"] }),
  });
  if (q.isPending) return <div className="grid place-items-center py-16"><Spinner /></div>;
  if (q.error) return <ErrorText error={q.error} />;
  const { enabled, domain, apps } = q.data;
  return (
    <>
      <PageHeader
        title="Web apps"
        description={`Apps people's assistants published${domain ? ` at *.${domain}` : ""}. Each runs in its owner's own workspace; visitors sign in first. Who may publish, and share outside the company, is set per group (People & groups → a group → Access).`}
      />
      <ErrorText error={remove.error} />
      {!enabled ? (
        <Card className="px-5 py-8 text-center text-sm text-[var(--muted)]">Web apps aren't set up for this install (it needs an apps domain).</Card>
      ) : !apps.length ? (
        <Card className="px-5 py-8 text-center text-sm text-[var(--muted)]">No published apps yet.</Card>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-[var(--line)] text-xs text-[var(--muted)]">
              <tr><th className="px-4 py-2.5 font-medium">App</th><th className="px-4 py-2.5 font-medium">Owner</th><th className="px-4 py-2.5 font-medium">Who can open it</th><th className="px-4 py-2.5 font-medium">Domains</th><th className="px-4 py-2.5" /></tr>
            </thead>
            <tbody>
              {apps.map((a) => (
                <tr key={a.id} className="border-b border-[var(--line)] last:border-0">
                  <td className="px-4 py-3">
                    <div className="font-medium">{a.title}</div>
                    <a href={a.url} target="_blank" rel="noreferrer" className="text-xs text-[var(--color-accent)] hover:underline">{a.url.replace(/^https:\/\//, "")}</a>
                  </td>
                  <td className="px-4 py-3">{a.owner?.name}<div className="text-xs text-[var(--muted)]">{a.owner?.email}</div></td>
                  <td className="px-4 py-3">
                    {PUBLIC.includes(a.effectiveAccess) ? <Badge tone="accent">{accessLabel(a.effectiveAccess, brand.name)}</Badge> : accessLabel(a.effectiveAccess, brand.name).replace(/^Only me$/, "Only the owner").replace(/^My group$/, "The owner's group")}
                  </td>
                  <td className="px-4 py-3 text-xs">{a.domains.length ? a.domains.map((d) => `${d.domain}${d.status === "active" ? "" : ` (${d.status})`}`).join(", ") : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="danger" className="whitespace-nowrap" disabled={remove.isPending} onClick={() => confirm(`Take ${a.title} offline? ${a.owner?.name ?? "Its owner"} keeps the files.`) && remove.mutate(a.id)}>Take offline</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}
